import Button from 'tailwind-components/Button/Button';
import HeaderFilterToolTip from '../HeaderFilter/HeaderFilterToolTip';
import Modal from 'tailwind-components/Modal/Modal';
import ModalActionConfirmation from 'components/ModalActionConfirmation/ModalActionConfirmation';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import TakeActionModal from '../Modals/TakeAction/TakeActionModal';
import { getCompany } from 'helpers/getCompanyVariables';
import icons from 'constants/icons';
import style from '../DashboardCustomer.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { formatMoney } from 'utils/utils';

import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

const company = getCompany();

import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import {
  downloadAllCustomersData,
  revalidatePolicy
} from 'state/actions/healthSherpa';
import {
  getCarrierAPIName,
  getConfirmedStates,
  getPendingStates,
  isAgentAppointedWithCarrier
} from 'helpers/appointments/appointments';
import { isCarrierDisabledByAgencyPreferences } from 'helpers/carriers/carriers';

const showMoreKeys = [
  {
    key: 'Plan',
    label: 'Plan',
    style: {
      width: '200px'
    }
  },
  {
    key: 'Gross',
    label: 'Gross',
    isCurrency: true
  },
  {
    key: 'Net',
    label: 'Net',
    isCurrency: true
  },
  {
    key: 'Residence',
    label: 'Residence',
    style: {
      width: '200px'
    }
  },
  {
    key: 'AssistorName',
    label: 'Writing Agent Name'
  },
  {
    key: 'AssistorAgency',
    label: 'Agency'
  },
  {
    key: 'email',
    label: 'Email'
  },
  {
    key: 'phone',
    label: 'Phone #'
  },
  {
    key: 'residence',
    secondaryKey: 'zipCode',
    label: 'Zip Code'
  }
];

const showDateKeys = [
  {
    key: 'Created',
    label: 'Submission'
  }
];

const CustomersTable = ({
  data_for_table,
  agent_policies_count,
  setCurrentPageSize,
  setCurrentPage,
  currentPage,
  formData,
  user,
  setTriggerFetch,
  setPotentialMatchesData,
  getPotentialMatches,
  activePanel,
  setActivePanel,
  applyFilters,
  setLocalLoader,
  createAppointmentRequest,
  handleChangeSelectValue,
  selectedFilterData = null,
  potentialMatchesCount
}) => {
  const dispatch = useDispatch();

  const { carriersArray, requestedAppointmentsByCarrier } = useSelector(
    state => state.appointments
  );
  const { isDownloadingReport } = useSelector(state => state.commissions);

  const [activePolicyData, setActivePolicyData] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50
  });

  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [activeCarrier, setActiveCarrier] = useState({
    name: '',
    id: '',
    carrier: '',
    confirmed: [],
    pending: [],
    carrierConsidersStates: false,
    statesOffered: []
  });

  function handleCloseAppointmentModal() {
    setActiveCarrier({
      name: '',
      id: '',
      carrier: '',
      confirmed: [],
      pending: [],
      carrierConsidersStates: false,
      statesOffered: []
    });
    setActivePolicyData({});
  }

  useEffect(() => {
    if (currentPage - 1 !== pagination.pageIndex) {
      setPagination(current => {
        return {
          ...current,
          pageIndex: currentPage - 1
        };
      });
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPage - 1 !== pagination.pageIndex) {
      setTriggerFetch(current => current + 1);
      setCurrentPage(pagination.pageIndex + 1);
    }
    setActivePanel({});
  }, [pagination.pageIndex]);

  useEffect(() => {
    setTriggerFetch(current => current + 1);
    setCurrentPageSize(pagination.pageSize);
    setActivePanel({});
  }, [pagination.pageSize]);

  const revalidateHSPolicy = useCallback((HsPolicyId, carrierName) => {
    setLocalLoader(true);
    dispatch(
      revalidatePolicy(
        function loader() {
          setTriggerFetch(current => current + 1);
        },
        () => {},
        HsPolicyId,
        {
          carrierName
        }
      )
    );
  }, []);

  const onMarketplaceClick = useCallback(
    (carrierData, ignored) => {
      if (!carrierData) return;

      const name = carrierData.name;

      const carrierNoSpaces = getCarrierAPIName(name);

      const carrierReqAppts = requestedAppointmentsByCarrier[name];

      const carrierConsidersStates = carrierData.enableStateRequestsIfAppointed
        ? isAgentAppointedWithCarrier(name, user)
        : carrierData.considerStates === 'Yes' &&
          carrierData.statesOffered.length > 0;

      const confirmed = carrierConsidersStates
        ? getConfirmedStates(name, user)
        : Array.isArray(user[carrierNoSpaces])
        ? user[carrierNoSpaces]
        : !!user[carrierNoSpaces];

      const pending = carrierConsidersStates
        ? getPendingStates(carrierReqAppts, confirmed)
        : carrierReqAppts.length > 0;

      const hasOpenTransferRequests = carrierReqAppts.some(
        appt => !!appt?.request_type.includes('Transfer')
      );

      const isAgencyDisabled = isCarrierDisabledByAgencyPreferences(
        carrierData,
        user
      );

      setActiveCarrier({
        ...activeCarrier,
        id: carrierData.id,
        name,
        pending,
        confirmed,
        carrier: name,
        carrierData: carrierData,
        statesOffered: carrierData.statesOffered,
        carrierConsidersStates,
        hasOpenTransferRequests,
        areApptsDisabled: carrierData.appointmentsDisabled,
        isAgencyDisabled,
        ignored
      });
    },
    [requestedAppointmentsByCarrier, user]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Client',
        accessorKey: 'Client',
        size: 140,
        enableColumnFilter: false,
        flexGrow: 1,
        /* eslint react/prop-types: 0 */
        Cell: ({ cell }) => (
          <div
            style={{
              color: '#2564B3'
            }}
          >
            {cell.getValue()}
          </div>
        )
      },
      {
        header: 'Carrier',
        accessorKey: 'Carrier',
        size: 120,
        enableColumnFilter: false,
        flexGrow: 1
      },
      {
        header: 'Carrier Policy #',
        accessorKey: 'CarrierPolicyNumber',
        size: 150,
        enableColumnFilter: false
      },
      {
        header: 'Issuer Sub #',
        accessorKey: 'IssuerSubID',
        size: 140,
        enableColumnFilter: false
      },
      {
        header: 'FFM App #',
        accessorKey: 'FFMAppID',
        size: 140,
        enableColumnFilter: false
      },
      {
        header: 'State',
        accessorKey: 'State',
        size: 70,
        enableColumnFilter: false,
        enableResizing: false
      },
      {
        header: 'NPN',
        accessorKey: 'AssistorNpn',
        size: 90,
        enableResizing: false,
        enableColumnFilter: false
      },
      {
        header: 'AOR Name',
        accessorKey: 'AorName',
        enableResizing: false,
        Filter: ({}) => {
          const options = ['Active', 'Inactive'];

          return (
            <HeaderFilterToolTip
              forceUpdateProp={selectedFilterData}
              applyFilters={applyFilters}
              initialData={formData.agentStatus}
              clearFilters={() => handleChangeSelectValue([], 'agentStatus')}
              statusName='AOR'
            >
              <div
                style={{
                  minWidth: '200px',
                  padding: '16px 16px 0px 16px'
                }}
              >
                <FormGroup>
                  {options.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={formData.agentStatus.includes(option)}
                        />
                      }
                      label={option}
                      onChange={() =>
                        handleChangeSelectValue(
                          formData.agentStatus.includes(option)
                            ? formData.agentStatus.filter(o => o !== option)
                            : [...formData.agentStatus, option],
                          'agentStatus'
                        )
                      }
                    />
                  ))}
                </FormGroup>
              </div>
            </HeaderFilterToolTip>
          );
        },
        flexGrow: 1,
        size: 120
      },
      {
        header: 'Effective',
        accessorKey: 'Effective',
        enableResizing: false,
        size: 100,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const date = cell.getValue()?.split('T')[0]?.split('-') ?? '';
          const year = date[0],
            month = date[1],
            dt = date[2];

          return (
            <div>
              {year > 2100 ? 'N/A' : date ? `${month}-${dt}-${year}` : ''}
            </div>
          );
        }
      },
      {
        header: 'Carrier Appt Status',
        accessorKey: 'CarrierApptStatus',
        size: 200,
        flexGrow: 1,
        enableResizing: false,
        muiTableHeadCellProps: { 'data-tour': 'submissions-carrier-status' },
        Filter: ({}) => {
          const options = ['Appointed', 'Not Appointed', 'Ignored', 'N/A'];

          return (
            <HeaderFilterToolTip
              forceUpdateProp={selectedFilterData}
              applyFilters={applyFilters}
              initialData={formData.carrierApptStatus}
              clearFilters={() =>
                handleChangeSelectValue([], 'carrierApptStatus')
              }
            >
              <div
                style={{
                  minWidth: '200px',
                  padding: '16px 16px 0px 16px'
                }}
              >
                <FormGroup>
                  {options.map(option => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={formData.carrierApptStatus.includes(option)}
                        />
                      }
                      label={option}
                      onChange={() =>
                        handleChangeSelectValue(
                          formData.carrierApptStatus.includes(option)
                            ? formData.carrierApptStatus.filter(
                                o => o !== option
                              )
                            : [...formData.carrierApptStatus, option],
                          'carrierApptStatus'
                        )
                      }
                    />
                  ))}
                </FormGroup>
              </div>
            </HeaderFilterToolTip>
          );
        },
        Cell: ({ cell }) => {
          const isComplete = !cell.row.original.incomplete;
          const appointmentStatus = cell.row.original.CarrierApptStatus;

          const carrierData = carriersArray.find(
            carrier => carrier.id === cell.row.original.CarrierId
          );

          const ACTIONABLE_APPOINTMENT_STATUSES = new Set([
            'NotAppointed',
            'Ignore'
          ]); // possible status values --> NotAppointed, Ignore, N/A, Appointed, InProgress

          const isAppointmentStatusValid =
            ACTIONABLE_APPOINTMENT_STATUSES.has(appointmentStatus);

          const shouldDisplayActionButton =
            isComplete && isAppointmentStatusValid;

          return (
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              <CustomCells
                Value={isComplete ? cell.getValue() : 'N/A'}
                Column='CarrierApptStatus'
              />
              {shouldDisplayActionButton && (
                <Button
                  isLoading={carriersArray.length === 0}
                  text='Take Action'
                  size='small'
                  className={style.buttonNoWrap}
                  variant='primaryDarkened'
                  onClick={() => {
                    onMarketplaceClick(
                      carrierData,
                      appointmentStatus === 'Ignore'
                    );
                    setActivePolicyData(cell.row.original);
                  }}
                />
              )}
            </div>
          );
        }
      },
      ...(company === 'oneill'
        ? [
            {
              header: 'BOB Status',
              accessorKey: 'BobStatus',
              enableResizing: false,
              size: 200,
              flexGrow: 1,
              Filter: ({}) => {
                const options = [
                  'Matched',
                  '>2 Weeks Past',
                  'Pending Match',
                  'Ignored',
                  'Check AOR',
                  'Manually Unmatched',
                  'Manually Matched'
                ];

                return (
                  <HeaderFilterToolTip
                    forceUpdateProp={selectedFilterData}
                    initialData={formData.bobFilter}
                    applyFilters={applyFilters}
                    clearFilters={() =>
                      handleChangeSelectValue([], 'bobFilter')
                    }
                  >
                    <div
                      style={{
                        minWidth: '200px',
                        padding: '16px 16px 0px 16px'
                      }}
                    >
                      <FormGroup>
                        {options.map(option => (
                          <FormControlLabel
                            key={option}
                            control={
                              <Checkbox
                                checked={formData.bobFilter.includes(option)}
                              />
                            }
                            label={option}
                            onChange={() =>
                              handleChangeSelectValue(
                                formData.bobFilter.includes(option)
                                  ? formData.bobFilter.filter(o => o !== option)
                                  : [...formData.bobFilter, option],
                                'bobFilter'
                              )
                            }
                          />
                        ))}
                      </FormGroup>
                    </div>
                  </HeaderFilterToolTip>
                );
              },
              Cell: ({ cell }) => {
                const date =
                  (
                    cell.row.original.bob_status_updated_at ??
                    cell.row.original.created_at
                  )
                    ?.split('T')[0]
                    ?.split('-') ?? '';
                const year = date[0],
                  month = date[1],
                  dt = date[2];

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        flexDirection: 'row',
                        width: '100%'
                      }}
                    >
                      <CustomCells Value={cell.getValue()} Column='BobStatus' />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        flexDirection: 'row',
                        width: '100%'
                      }}
                    >
                      <Button
                        onClick={() =>
                          revalidateHSPolicy(
                            cell.row.original.HealthSherpaId,
                            cell.row.original.Carrier
                          )
                        }
                        icon={<RefreshIcon />}
                        size='small'
                      />

                      <div>
                        <span
                          style={{
                            fontStyle: 'italic',
                            fontSize: '12px'
                          }}
                        >
                          Last update:{' '}
                        </span>
                        {month && (
                          <span
                            style={{
                              fontSize: '12px'
                            }}
                          >
                            {month}-{dt}-{year}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
            },
            {
              header: 'BOB Potential Matches',
              accessorKey: 'BOBPotentialMatches',
              enableResizing: false,
              enableColumnFilter: false,
              size: 200,
              Cell: ({ cell }) => {
                const pontentialMatches = potentialMatchesCount.find(
                  potential =>
                    potential.hsPolicyId ===
                    cell.row.original.Policies_healthSherpaId
                );

                const showViewButton =
                  !cell.row.original.incomplete &&
                  (pontentialMatches?.potentialBobMatches.length > 0 ||
                    ['Matched', 'ManuallyMatched'].includes(
                      cell.row.original.BobStatus
                    ));

                const showLoading =
                  !cell.row.original.incomplete &&
                  [
                    'PendingMatch',
                    'TwoWeeksPast',
                    'Ignored',
                    'ManuallyUnmatched',
                    'AssignedToOther'
                  ].includes(cell.row.original.BobStatus) &&
                  !pontentialMatches;

                return showViewButton || showLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Button
                      text='View'
                      size='small'
                      className={style.buttonNoWrap}
                      variant='primaryDarkened'
                      isLoading={showLoading}
                      loadingColor={'white'}
                      onClick={() => {
                        if (!cell.row.original.HealthSherpaId) return;

                        getPotentialMatches(
                          cell.row.original.HealthSherpaId,
                          cell.row.original.AssistorNpn,
                          cell.row.original.BobStatus
                        );
                        setPotentialMatchesData({
                          HsPolicyId: cell.row.original.HealthSherpaId,
                          BobStatus: cell.row.original.BobStatus,
                          npn: cell.row.original.AssistorNpn,
                          currentBobMatchId: '',
                          potentialBobMatches: [],
                          originalPolicy: {
                            Client: cell.row.original.Client,
                            Carrier: cell.row.original.Carrier,
                            CarrierPolicyNumber:
                              cell.row.original.CarrierPolicyNumber,
                            FFMAppID: cell.row.original.FFMAppID,
                            IssuerSubID: cell.row.original.IssuerSubID,
                            State: cell.row.original.State,
                            Npn: cell.row.original.AssistorNpn,
                            Gross: cell.row.original.Gross,
                            Net: cell.row.original.Net,
                            Residence: cell.row.original.Residence,
                            AorName: cell.row.original.AorName,
                            AssistorName: cell.row.original.AssistorName,
                            AssistorAgency: cell.row.original.AssistorAgency,
                            Created: cell.row.original.Created,
                            email: cell.row.original.email || '-',
                            phone: cell.row.original.phone || '-',
                            zipCode: cell.row.original.residence?.zipCode || '-'
                          }
                        });
                      }}
                    />
                  </div>
                ) : (
                  <span style={{ fontSize: '12px' }}>
                    No Potential Matches Found
                  </span>
                );
              }
            }
          ]
        : [])
    ],
    [
      carriersArray,
      onMarketplaceClick,
      formData,
      selectedFilterData,
      potentialMatchesCount
    ]
  );

  const handleDownloadRows = useCallback(() => {
    dispatch(downloadAllCustomersData(formData));
  }, [formData]);

  const table = useMaterialReactTable({
    columns,
    data: [...data_for_table],
    enableColumnActions: false,
    enableColumnResizing: true,
    enableTopToolbar: false,
    enableSorting: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    initialState: { showColumnFilters: true },
    paginationDisplayMode: 'pages',
    rowCount: Number(agent_policies_count),
    onPaginationChange: setPagination,
    onExpandedChange: active => {
      setActivePanel(active);
    },
    state: {
      pagination: pagination,
      expanded: activePanel
    },
    muiPaginationProps: {
      rowsPerPageOptions: [50, 100],
      showFirstButton: false,
      showLastButton: false,
      variant: 'outlined',
      style: {
        paddingRight: '60px'
      }
    },
    muiTablePaperProps: {
      elevation: 0, //change the mui box shadow
      style: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 0
      },
      className: style.tableHeight
    },
    muiTableContainerProps: {
      id: 'customers-table-body',
      style: {
        flex: 1,
        scrollbarWidth: 'auto'
      }
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: ''
      }
    },
    renderBottomToolbarCustomActions: ({}) => (
      <>
        <Button
          isDisabled={isDownloadingReport}
          isLoading={isDownloadingReport}
          text='Export All'
          variant='secondary'
          onClick={() => setIsDisclaimerOpen(true)}
        />
        <Modal
          size='medium'
          title='Disclaimer'
          active={isDisclaimerOpen}
          className={style.modal}
          closeModal={() => setIsDisclaimerOpen(false)}
        >
          <ModalActionConfirmation
            onCancel={() => setIsDisclaimerOpen(false)}
            content={
              <p className={style.disclaimerText}>
                This data <strong>should not</strong> be used as a Book of
                Business report. For an accurate list of policies in your Book
                of Business, please refer to your carrier portals for the most
                up-to-date information.
              </p>
            }
            primaryButtonText={'Continue with Export'}
            onPrimaryConfirm={handleDownloadRows}
            isLoading={isDownloadingReport}
            isDisabled={isDownloadingReport}
          />
        </Modal>
      </>
    ),
    muiTableHeadCellProps: {
      style: {
        backgroundColor: '#FAFAFA',
        padding: '10px 5px',
        justifyContent: 'center'
      }
    },
    muiTableBodyCellProps: {
      style: {
        padding: '10px 5px'
      }
    },
    manualPagination: true,
    renderEmptyRowsFallback: () => (
      <h2
        style={{
          color: 'grey',
          textAlign: 'center',
          fontWeight: '400',
          fontSize: 14,
          padding: 15
        }}
      >
        No customers related to this agent
      </h2>
    ),
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }), //only 1 detail panel open at a time
      sx: {
        transform: row.getIsExpanded() ? 'rotate(0deg)' : 'rotate(-0deg)',
        transition: 'transform 0.2s'
      },
      disabled: false
    }),
    enableExpanding: true,
    enableExpandAll: false,
    muiTableHeadCellProps: ({ column }) => {
      const { columnDef } = column;

      const hasFilters = columnDef.Filter;

      if (columnDef.id === 'mrt-row-spacer')
        return {
          style: {
            maxWidth: '0px'
          }
        };
      return {
        className: `${
          style[`react-table-column-flex-grow-${columnDef.flexGrow}`]
        } ${hasFilters ? style['react-table-head-content'] : ''}`,
        style: {
          display: 'flex',
          flexDirection: 'row',
          gap: '10px'
        }
      };
    },
    renderDetailPanel: ({ row }) => {
      return (
        <div
          data-tour='submissions-row-detail'
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '25px'
          }}
        >
          {showMoreKeys.map(
            ({ key, label, style, isCurrency, secondaryKey }) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  ...style
                }}
              >
                <label
                  style={{
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {label}
                </label>
                <p>
                  {isCurrency
                    ? formatMoney(Number(row?.original[key]))
                    : secondaryKey
                    ? row?.original[key][secondaryKey] || '-'
                    : row?.original[key] || '-'}
                </p>
              </div>
            )
          )}
          {showDateKeys.map(({ key, label }) => {
            const date = row?.original[key]?.split('T')[0]?.split('-') ?? '';
            const year = date[0],
              month = date[1],
              dt = date[2];

            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <label
                  style={{
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {label}
                </label>
                <p>
                  {year > 2100 ? 'N/A' : date ? `${month}-${dt}-${year}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      );
    },
    muiDetailPanelProps: () => ({
      sx: () => ({
        backgroundColor: 'rgba(227,246,255,1)'
      })
    })
  });

  return (
    <>
      <div
        data-tour='submissions-table'
        style={{
          width: '100%',
          flex: 1,
          display: 'flex'
        }}
      >
        <MaterialReactTable table={table} />
      </div>
      {activeCarrier.id && (
        <TakeActionModal
          activeCarrier={activeCarrier}
          handleCloseModal={handleCloseAppointmentModal}
          createAppointmentRequest={createAppointmentRequest}
          user={user}
          setLocalLoader={setLocalLoader}
          applyFilters={applyFilters}
          activePolicyData={activePolicyData}
        />
      )}
    </>
  );
};

CustomersTable.propTypes = {
  data_for_table: PropTypes.array,
  agent_policies_count: PropTypes.number,
  setCurrentPage: PropTypes.func,
  setCurrentPageSize: PropTypes.func,
  currentPage: PropTypes.number,
  setTriggerFetch: PropTypes.func,
  setPotentialMatchesData: PropTypes.func,
  getPotentialMatches: PropTypes.func,
  setActivePanel: PropTypes.func,
  activePanel: PropTypes.object,
  applyFilters: PropTypes.func,
  user: PropTypes.object,
  createAppointmentRequest: PropTypes.func,
  formData: PropTypes.object,
  setLocalLoader: PropTypes.func,
  handleChangeSelectValue: PropTypes.func,
  selectedFilterData: PropTypes.any,
  potentialMatchesCount: PropTypes.array
};

export default memo(CustomersTable);

const CircleIcon = icons['circle'];
const WarningIcon = icons['warning'];
const RefreshIcon = icons['refresh'];
const NotApplicable = icons['doNotDisturb'];

const CustomCellsTop = ({ Value, Column, Small }) => {
  const colorIconConfig = {
    CarrierApptStatus: {
      NotAppointed: {
        color: '#E85C53',
        icon: <WarningIcon fontSize='small' />,
        text: 'Not Appointed'
      },
      Appointed: {
        color: '#5DBB29',
        icon: <CircleIcon fontSize='small' />,
        text: 'Appointed'
      },
      Ignore: {
        color: '#909090',
        icon: <WarningIcon fontSize='small' />,
        text: 'Ignore'
      },
      InProgress: {
        color: '#E0DA1F',
        icon: <WarningIcon fontSize='small' />,
        text: 'In Progress'
      },
      'N/A': {
        color: '#000',
        icon: <NotApplicable fontSize='small' />,
        text: 'N/A'
      }
    },
    BobStatus: {
      Matched: {
        color: '#5DBB29',
        icon: <CircleIcon fontSize='small' />,
        text: 'Matched'
      },
      ManuallyMatched: {
        color: '#008000',
        icon: <CircleIcon fontSize='small' />,
        text: 'Manually Matched'
      },
      ManuallyUnmatched: {
        color: '#C70039',
        icon: <WarningIcon fontSize='small' />,
        text: 'Manually Unmatched'
      },
      TwoWeeksPast: {
        color: '#E85C53',
        icon: <WarningIcon fontSize='small' />,
        text: '>2 Weeks Past'
      },
      PendingMatch: {
        color: '#E4D00A',
        icon: <WarningIcon fontSize='small' />,
        text: 'Pending Match'
      },
      AssignedToOther: {
        color: '#F09F0C',
        icon: <WarningIcon fontSize='small' />,
        text: 'Check AOR'
      },
      Ignore: {
        color: '#909090',
        icon: <WarningIcon fontSize='small' />,
        text: 'Ignore'
      },
      EmailedCarrier: {
        color: '#000000',
        icon: <WarningIcon fontSize='small' />,
        text: 'Emailed Carrier'
      }
    }
  };

  return (
    <>
      <div
        style={{
          color: colorIconConfig[Column][Value]?.color ?? '',
          padding: '0',
          margin: '0',
          lineHeight: '1',
          borderRadius: '13px',
          display: 'flex',
          alignItems: 'center',
          ...(Small
            ? {
                fontSize: '12px',
                fontWeight: 500,
                gap: '4px'
              }
            : {
                height: '24px',
                width: '100%',
                gap: '10px'
              })
        }}
      >
        {colorIconConfig[Column][Value]?.icon}
        {!Small && (colorIconConfig[Column][Value]?.text ?? '')}
      </div>
    </>
  );
};

const CustomCells = memo(CustomCellsTop);

CustomCells.propTypes = {
  Value: PropTypes.string,
  Column: PropTypes.string,
  Small: PropTypes.bool
};
